import type { ShowHistory } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { omit } from 'lodash';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useApiStore } from '../api/api-store';
import { TrackApi } from './track-api';
import { TrackShowHistoryApi } from './track-show-history-api';
import { TrackSubscriptionsApi } from './track-subscriptions-api';
import type { TrackState } from './types';

const initialState = {
  loading: {
    mostRecentTrackHistory: false,
    recentTrackHistory: false,
    currentlyPlaying: false,
    showHistoriesForShow: {},
    tracksForShowHistory: {},
    track: {},
    subscriptions: false,
    updateSubscriptionsById: {},
    initial: true,
    showsFeaturedByTrack: {},
  },

  trackPlayer: {
    loading: false,
    registered: false,
    isPlaying: false,
  },

  currentlyPlaying: null,
  currentlyAiringShow: null,
  recentTrackHistory: [],
  showHistories: {},
  trackHistoryByShowHistory: {},
  tracksById: {},
  trackSubscriptionsById: null,

  fetchedFinalRecentTrackHistoryItem: false,
  hasMoreTrackHistory: true,
  lastDataFetchedFromApiAt: null,
  showsFeaturedByTrack: {},
} as const satisfies TrackState;

type UtilityFunctions = {
  startup: () => Promise<void>;
  teardown: () => void;
  updateTrackPlayerState: (key: keyof TrackState['trackPlayer'], value: boolean) => void;
  clearShowHistoryById: (id: Uuid<ShowHistory>) => void;
  setInitialLoadCompleted: () => void;
};

export const useTrackStore = create<
  TrackState & TrackApi & TrackSubscriptionsApi & TrackShowHistoryApi & UtilityFunctions
>()(
  persist(
    (set, get) => ({
      ...initialState,
      ...TrackApi(get, set),
      ...TrackSubscriptionsApi(get, set),
      ...TrackShowHistoryApi(get, set),
      startup: async () => {
        const { fetchCurrentlyPlaying, fetchMostRecentTrackHistory } = get();
        await fetchCurrentlyPlaying();
        await fetchMostRecentTrackHistory();
      },
      teardown: () => {
        set({
          showHistories: {},
          trackHistoryByShowHistory: {},
        } as const satisfies Partial<TrackState>);
      },
      updateTrackPlayerState: (key, value) => {
        set(
          (s) =>
            ({
              trackPlayer: {
                ...s.trackPlayer,
                [key]: value,
              },
            }) as const satisfies Partial<TrackState>,
        );
      },
      clearShowHistoryById: (id) => {
        set(
          (s) =>
            ({
              showHistories: {
                ...s.showHistories,
                [id]: undefined,
              },
              trackHistoryByShowHistory: {
                ...s.trackHistoryByShowHistory,
                [id]: undefined,
              },
            }) as const satisfies Partial<TrackState>,
        );
      },
      setInitialLoadCompleted: () =>
        set((s) => ({
          loading: {
            ...s.loading,
            initial: false,
          },
        })),
    }),
    {
      name: 'track-storage',
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          const store = useApiStore.getState().localStores.track;
          return store?.getItem(name) ?? null;
        },
        setItem: (name: string, value: string) => {
          const store = useApiStore.getState().localStores.track;
          store?.setItem(name, value);
        },
        removeItem: (name: string) => {
          const store = useApiStore.getState().localStores.track;
          store?.removeItem(name);
        },
      })),
      skipHydration: true,
      partialize: (state) => omit(state, 'loading', 'trackPlayer'),
    },
  ),
);
